/**
 * One client's ledger (T-C2's right card): who, since when, where they stand as one big figure,
 * the four acts and the „?" that explains them, and the history — date, entry, change, balance
 * after — newest first, with older pages on request.
 *
 * The figure is the ledger's own (the answer this history came with), never the list's: the two
 * are one fact, but the card must not show a standing from one read over a history from another.
 * The tab draws the card once the first client's history has answered. Opening another client
 * reads theirs in the card: it stands in its loaded shape — the head from the list's row, the acts,
 * the column heads — with bars where the figure and the rows will be. A table rather than a
 * list: the four columns are a table's, and it carries a caption naming whose history it is.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { displayInitials } from '~/shared/lib/displayInitials';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import skeleton from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import {
  creditActClass,
  creditStanding,
  type CreditStanding,
  LEDGER_ACTS,
  LedgerActDialog,
  standingUnit,
} from '~/features/credit-ledger';

import { useClientLedger } from '../lib/useClientLedger';
import { forgivableWords } from '../model/creditLedger';
import { numericDate } from '../model/calendarWords';
import { type ClientBalanceRow } from '../model/creditLedger';
import type { CivilDay } from '../model/trainerClock';
import { LedgerHelpDialog } from './LedgerHelpDialog';
import frame from './bookingTab.module.css';
import styles from './LedgerTab.module.css';

interface LedgerCardProps {
  readonly row: ClientBalanceRow;
  readonly name: string;
  readonly context: { readonly zone: string; readonly today: CivilDay; readonly nowMs: number };
  readonly announce: (message: string) => void;
}

/** How many history rows the wait reserves — the canon's first screenful. */
const RESERVED_ROWS = 6;

export function LedgerCard({ row, name, context, announce }: LedgerCardProps) {
  const { t } = useTranslation();
  const ledger = useClientLedger(row.customerId, context, announce);
  const [helpOpen, setHelpOpen] = useState(false);
  const { gate, content } = ledger;
  const waiting = content.kind === 'waiting';
  const bar = gate.skeleton ? String(skeleton.bar) : '';
  const standing: CreditStanding =
    content.kind === 'ready' ? creditStanding(content.standing) : row.standing;

  return (
    <section
      className={`${String(frame.card)} ${String(styles.ledger)}`}
      aria-labelledby="booking-ledger-name"
      aria-busy={gate.waiting}
    >
      {gate.waiting ? (
        <span className={hidden.srOnly}>{t('web:booking.credits.loading')}</span>
      ) : null}
      <div className={styles.ledgerHead}>
        <AvatarChip initials={displayInitials(name)} avatarUrl={null} />
        <div className={styles.ledgerWho}>
          <h2 id="booking-ledger-name" className={styles.ledgerName}>
            {name}
          </h2>
          <span
            className={
              waiting
                ? `${String(styles.ledgerSince)} ${String(styles.hiddenInk)} ${bar}`
                : styles.ledgerSince
            }
          >
            {content.kind === 'ready' && content.since !== null
              ? t('web:booking.credits.ledger.since', {
                  date: numericDate(content.since, context.zone),
                })
              : ' '}
          </span>
        </div>
        <div className={styles.figure}>
          <b
            className={
              waiting
                ? `${String(styles.figureValue)} ${String(styles.hiddenInk)} ${bar}`
                : styles.figureValue
            }
          >
            {standing.count}
          </b>
          <span
            className={
              waiting
                ? `${String(styles.figureUnit)} ${String(styles.hiddenInk)} ${bar}`
                : styles.figureUnit
            }
          >
            {standingUnit(standing, t)}
          </span>
        </div>
      </div>

      <div className={styles.acts}>
        {LEDGER_ACTS.map((act) => (
          <button
            key={act}
            type="button"
            className={`${creditActClass} ${String(styles.act)}`}
            aria-disabled={content.kind === 'ready' ? undefined : true}
            onClick={() => {
              // A settlement with nothing owed still opens: its dialog says why it cannot be sent,
              // beside its act, where a sighted trainer reads it too.
              if (content.kind === 'ready') ledger.act.open(act);
            }}
          >
            {t(`web:booking.trainer.credits.acts.${act}`)}
          </button>
        ))}
        <button
          type="button"
          className={`${creditActClass} ${String(styles.helpButton)}`}
          aria-label={t('web:booking.trainer.credits.helpA11y')}
          onClick={() => {
            setHelpOpen(true);
          }}
        >
          ?
        </button>
      </div>
      <p className={styles.historyNote}>{t('web:booking.credits.ledger.history')}</p>
      {content.kind === 'ready' && ledger.sessionsFailed ? (
        <p className={styles.sessionsFailed} role="status">
          <span>{t('web:booking.credits.ledger.sessionsFailed')}</span>
          <button type="button" className={styles.inlineRetry} onClick={ledger.retrySessions}>
            {t('web:booking.trainer.credits.retry')}
          </button>
        </p>
      ) : null}

      {content.kind === 'failed' ? (
        <div className={`${String(frame.failed)} ${gate.reveal}`} role="alert">
          <p className={frame.failedTitle}>{t('web:booking.credits.ledger.unreadTitle')}</p>
          <button type="button" className={creditActClass} onClick={ledger.retry}>
            {t('web:booking.trainer.credits.retry')}
          </button>
        </div>
      ) : (
        <table className={styles.table}>
          <caption className={hidden.srOnly}>
            {t('web:booking.credits.ledger.caption', { name })}
          </caption>
          <thead>
            <tr>
              <th scope="col" className={styles.colDate}>
                {t('web:booking.credits.ledger.date')}
              </th>
              <th scope="col">{t('web:booking.credits.ledger.entry')}</th>
              <th scope="col" className={styles.colNumber}>
                {t('web:booking.credits.ledger.change')}
              </th>
              <th scope="col" className={styles.colNumber}>
                {t('web:booking.credits.ledger.after')}
              </th>
            </tr>
          </thead>
          <tbody className={waiting ? undefined : gate.reveal}>
            {content.kind === 'waiting' ? (
              Array.from({ length: RESERVED_ROWS }, (_, index) => (
                <tr key={index} className={styles.row} aria-hidden="true">
                  <td className={styles.colDate}>
                    <span className={`${String(styles.barShort)} ${bar}`} />
                  </td>
                  <td>
                    <span className={`${String(styles.barTitle)} ${bar}`} />
                    <span className={`${String(styles.barMeta)} ${bar}`} />
                  </td>
                  <td className={styles.colNumber}>
                    <span className={`${String(styles.barShort)} ${bar}`} />
                  </td>
                  <td className={styles.colNumber}>
                    <span className={`${String(styles.barShort)} ${bar}`} />
                  </td>
                </tr>
              ))
            ) : content.rows.length === 0 ? (
              <tr className={styles.row}>
                <td colSpan={4} className={styles.emptyCell}>
                  <b className={styles.entryTitle}>{t('web:booking.trainer.credits.emptyTitle')}</b>
                  <span className={styles.entryMeta}>
                    {t('web:booking.trainer.credits.emptyBody')}
                  </span>
                </td>
              </tr>
            ) : (
              content.rows.map((entry) => (
                <tr key={entry.key} className={styles.row}>
                  <td className={`${String(styles.colDate)} ${String(styles.day)}`}>{entry.day}</td>
                  <td>
                    <span className={styles.entryTitle}>{entry.title}</span>
                    {entry.meta === null ? null : (
                      <span className={styles.entryMeta}>{entry.meta}</span>
                    )}
                  </td>
                  <td
                    className={`${String(styles.colNumber)} ${String(styles.change)} ${entry.tone === 'plus' ? String(styles.plus) : ''}`}
                  >
                    {entry.amount}
                  </td>
                  <td className={`${String(styles.colNumber)} ${String(styles.afterCell)}`}>
                    {entry.after}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {content.kind === 'ready' && ledger.hasOlder ? (
        <div className={styles.older}>
          <button
            type="button"
            className={creditActClass}
            aria-busy={ledger.loadingOlder}
            onClick={ledger.loadOlder}
          >
            {ledger.loadingOlder
              ? t('web:booking.credits.ledger.olderLoading')
              : t('web:booking.credits.ledger.older')}
          </button>
          {ledger.olderFailed ? (
            <span className={styles.olderFailed} role="status">
              {t('web:booking.credits.ledger.olderFailed')}
            </span>
          ) : null}
        </div>
      ) : null}

      <LedgerActDialog
        draft={ledger.act.draft}
        clientName={name}
        standing={content.kind === 'ready' ? content.standing : { balance: 0, sessionsOwed: 0 }}
        forgiveness={{
          picks:
            ledger.forgivable === null
              ? null
              : ledger.forgivable.map((session) => ({
                  id: session.id,
                  ...forgivableWords(session, context.zone, t),
                })),
          failed: ledger.forgivableFailed,
          onRetry: ledger.retrySessions,
        }}
        sending={ledger.act.sending}
        locked={ledger.act.unanswered}
        refusal={ledger.act.refusal}
        onEdit={ledger.act.edit}
        onSend={ledger.act.send}
        onClose={ledger.act.close}
      />
      <LedgerHelpDialog
        open={helpOpen}
        onClose={() => {
          setHelpOpen(false);
        }}
      />
    </section>
  );
}
